import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createActivity, getClients, getClientMappedEmployees, createFollowUp, createTask } from '../lib/api';
import { formatApiError, getClientDisplayName } from '../lib/utils';
import { ACTIVITY_TYPES, TIME_SLOTS, BUSINESS_TIME_SLOTS, FOLLOWUP_TIME_SLOTS, CALL_STATUSES, MEETING_STATUSES, CALL_DURATIONS, MEETING_DURATIONS, FOLLOW_UP_TYPES, TASK_TYPES, PRIORITIES } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DatePicker } from '../components/ui/date-picker';
import { TimePicker } from '../components/ui/time-picker';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft } from '@phosphor-icons/react';

function getNearestTimeSlot(slots) {
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  let best = slots[0]?.value || '';
  let bestDiff = Infinity;
  for (const s of slots) {
    const [h, m] = s.value.split(':').map(Number);
    const diff = Math.abs(h * 60 + m - nowMins);
    if (diff < bestDiff) { bestDiff = diff; best = s.value; }
  }
  return best;
}

export default function ActivityFormPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [mappedEmployees, setMappedEmployees] = useState([]);
  const [showPostAction, setShowPostAction] = useState(false);
  const [postActionType, setPostActionType] = useState(null); // 'followup' or 'task'
  const [lastActivity, setLastActivity] = useState(null);
  const [postForm, setPostForm] = useState({ follow_up_type: '', task_type: '', due_date: '', due_time: '', priority: 'Medium', description: '', notes: '', assigned_to: '' });
  const [form, setForm] = useState({
    client_id: '',
    activity_type: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    description: '',
    call_status: '',
    call_duration: '',
    meeting_status: '',
    meeting_duration: '',
    assigned_to: '',
  });

  useEffect(() => {
    getClients().then(res => setClients(res.data)).catch(err => toast.error(formatApiError(err))).finally(() => setLoading(false));
  }, []);

  // Fetch mapped employees when client is selected
  useEffect(() => {
    if (form.client_id) {
      getClientMappedEmployees(form.client_id).then(res => setMappedEmployees(res.data)).catch(() => setMappedEmployees([]));
    } else {
      setMappedEmployees([]);
    }
  }, [form.client_id]);

  // Set default time when activity type changes
  useEffect(() => {
    if (form.activity_type === 'Call' || form.activity_type === 'Meeting') {
      if (!form.time) setForm(p => ({ ...p, time: getNearestTimeSlot(BUSINESS_TIME_SLOTS) }));
    } else if (form.activity_type === 'WhatsApp') {
      if (!form.time) setForm(p => ({ ...p, time: getNearestTimeSlot(TIME_SLOTS) }));
    }
  }, [form.activity_type]);

  const isCall = form.activity_type === 'Call';
  const isMeeting = form.activity_type === 'Meeting';
  const timeSlots = (isCall || isMeeting) ? BUSINESS_TIME_SLOTS : TIME_SLOTS;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error('Please select a client'); return; }
    if (!form.activity_type) { toast.error('Please select an activity type'); return; }
    if (isCall && !form.call_status) { toast.error('Please select call status'); return; }
    if (isMeeting && !form.meeting_status) { toast.error('Please select meeting status'); return; }
    setSaving(true);
    try {
      const res = await createActivity(form);
      toast.success('Activity logged successfully');
      // Check if eligible for follow-up/task prompt
      const isAnswered = form.activity_type === 'Call' && form.call_status === 'Answered';
      const isMet = form.activity_type === 'Meeting' && form.meeting_status === 'Met';
      if (isAnswered || isMet) {
        setLastActivity({ ...res.data, client_id: form.client_id });
        setShowPostAction(true);
      } else {
        navigate(`/clients/${form.client_id}`);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handlePostActionCreate = async () => {
    if (!lastActivity) return;
    setSaving(true);
    try {
      if (postActionType === 'followup') {
        await createFollowUp({
          client_id: lastActivity.client_id,
          follow_up_type: postForm.follow_up_type || 'General Follow-Up',
          due_date: postForm.due_date,
          due_time: postForm.due_time,
          priority: postForm.priority,
          description: postForm.description,
          assigned_to: form.assigned_to || undefined,
          source_activity_id: lastActivity.id,
        });
        toast.success('Follow-up created');
      } else {
        await createTask({
          client_id: lastActivity.client_id,
          task_type: postForm.task_type || 'General',
          due_date: postForm.due_date,
          priority: postForm.priority,
          description: postForm.description,
          assigned_to: form.assigned_to || undefined,
          source_activity_id: lastActivity.id,
        });
        toast.success('Task created');
      }
      setShowPostAction(false);
      navigate(`/clients/${lastActivity.client_id}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setSaving(false); }
  };



  if (loading) return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-[300px]" />
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="activity-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Log Activity
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <Card className="border-zinc-200 rounded-sm border-[#002FA7]/30 bg-blue-50/20">
          <CardContent className="p-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                Client <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                value={form.client_id}
                onValueChange={(v) => setForm(p => ({ ...p, client_id: v }))}
                options={clients.map(c => ({ value: c.id, label: getClientDisplayName(c) }))}
                placeholder="Select client"
                searchPlaceholder="Search clients..."
                data-testid="activity-client-select"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-sm">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Activity Details</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Type <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  value={form.activity_type}
                  onValueChange={(v) => setForm(p => ({ ...p, activity_type: v, call_status: '', call_duration: '', meeting_status: '', meeting_duration: '' }))}
                  options={ACTIVITY_TYPES}
                  placeholder="Select type"
                  data-testid="activity-type-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Date <span className="text-red-500">*</span></Label>
                <DatePicker
                  value={form.date}
                  onChange={(v) => setForm(p => ({ ...p, date: v }))}
                  maxDate={new Date().toISOString().split('T')[0]}
                  placeholder="Select date"
                  data-testid="activity-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Time <span className="text-red-500">*</span></Label>
                <TimePicker
                  value={form.time || ''}
                  onChange={(v) => setForm(p => ({ ...p, time: v }))}
                  slots={timeSlots}
                  placeholder="Select time"
                  data-testid="activity-time-select"
                />
              </div>
            </div>

            {/* Call-specific fields */}
            {isCall && (
              <div className="grid grid-cols-2 gap-6 pt-2 border-t border-zinc-100">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Call Status <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    value={form.call_status}
                    onValueChange={(v) => setForm(p => ({ ...p, call_status: v, call_duration: v !== 'Answered' ? '' : p.call_duration }))}
                    options={CALL_STATUSES}
                    placeholder="Select status"
                    data-testid="activity-call-status-select"
                  />
                </div>
                {form.call_status === 'Answered' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Call Duration</Label>
                    <SearchableSelect
                      value={form.call_duration}
                      onValueChange={(v) => setForm(p => ({ ...p, call_duration: v }))}
                      options={CALL_DURATIONS}
                      placeholder="Select duration"
                      data-testid="activity-call-duration-select"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Meeting-specific fields */}
            {isMeeting && (
              <div className="grid grid-cols-2 gap-6 pt-2 border-t border-zinc-100">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Meeting Status <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    value={form.meeting_status}
                    onValueChange={(v) => setForm(p => ({ ...p, meeting_status: v, meeting_duration: v !== 'Met' ? '' : p.meeting_duration }))}
                    options={MEETING_STATUSES}
                    placeholder="Select status"
                    data-testid="activity-meeting-status-select"
                  />
                </div>
                {form.meeting_status === 'Met' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Meeting Duration</Label>
                    <SearchableSelect
                      value={form.meeting_duration}
                      onValueChange={(v) => setForm(p => ({ ...p, meeting_duration: v }))}
                      options={MEETING_DURATIONS}
                      placeholder="Select duration"
                      data-testid="activity-meeting-duration-select"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Employee logged by */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Logged By</Label>
              <SearchableSelect
                value={form.assigned_to}
                onValueChange={(v) => setForm(p => ({ ...p, assigned_to: v }))}
                options={mappedEmployees.map(u => ({ value: u.id, label: u.name }))}
                placeholder={mappedEmployees.length ? "Select employee" : "Select client first"}
                searchPlaceholder="Search employees..."
                data-testid="activity-assigned-to"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                rows={3} className="rounded-sm" placeholder="What happened during this interaction?"
                data-testid="activity-description-input"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-sm">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="activity-submit-button">
            {saving ? 'Saving...' : 'Log Activity'}
          </Button>
        </div>
      </form>

      {/* Post-Activity Action Dialog */}
      <Dialog open={showPostAction} onOpenChange={(open) => { if (!open) { setShowPostAction(false); navigate(`/clients/${lastActivity?.client_id}`); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {form.activity_type} {form.activity_type === 'Call' ? 'Connected' : 'Completed'} — Create Follow-up or Task?
            </DialogTitle>
          </DialogHeader>
          {!postActionType ? (
            <div className="space-y-3 py-4">
              <p className="text-sm text-zinc-600">Would you like to create a follow-up or task from this activity?</p>
              <div className="flex gap-3">
                <Button className="flex-1 bg-[#002FA7] text-white" onClick={() => setPostActionType('followup')} data-testid="post-create-followup">
                  Create Follow-up
                </Button>
                <Button className="flex-1" variant="outline" onClick={() => setPostActionType('task')} data-testid="post-create-task">
                  Create Task
                </Button>
              </div>
              <Button variant="ghost" className="w-full text-zinc-500 text-sm" onClick={() => { setShowPostAction(false); navigate(`/clients/${lastActivity?.client_id}`); }}>
                Skip
              </Button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {postActionType === 'followup' && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Follow-up Type <span className="text-red-500">*</span></Label>
                  <SearchableSelect value={postForm.follow_up_type} onValueChange={(v) => setPostForm(p => ({ ...p, follow_up_type: v }))} options={FOLLOW_UP_TYPES.map(t => ({ value: t, label: t }))} placeholder="Select type" />
                </div>
              )}
              {postActionType === 'task' && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Task Type <span className="text-red-500">*</span></Label>
                  <SearchableSelect value={postForm.task_type} onValueChange={(v) => setPostForm(p => ({ ...p, task_type: v }))} options={TASK_TYPES.map(t => ({ value: t, label: t }))} placeholder="Select type" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Due Date <span className="text-red-500">*</span></Label>
                  <DatePicker value={postForm.due_date} onChange={(v) => setPostForm(p => ({ ...p, due_date: v }))} placeholder="Select date" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Due Time</Label>
                  <TimePicker value={postForm.due_time || ''} onChange={(v) => setPostForm(p => ({ ...p, due_time: v }))} slots={FOLLOWUP_TIME_SLOTS} placeholder="Select time" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Priority</Label>
                  <SearchableSelect value={postForm.priority} onValueChange={(v) => setPostForm(p => ({ ...p, priority: v }))} options={PRIORITIES.map(p => ({ value: p, label: p }))} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Assigned To</Label>
                  <SearchableSelect value={postForm.assigned_to || ''} onValueChange={(v) => setPostForm(p => ({ ...p, assigned_to: v }))} options={mappedEmployees.map(u => ({ value: u.id, label: u.name }))} placeholder={mappedEmployees.length ? "Select" : "Select client first"} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Description</Label>
                <Textarea value={postForm.description} onChange={(e) => setPostForm(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="rounded-sm" rows={2} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPostActionType(null)}>Back</Button>
                <Button onClick={handlePostActionCreate} disabled={saving} className="bg-[#002FA7] text-white">
                  {saving ? 'Creating...' : `Create ${postActionType === 'followup' ? 'Follow-up' : 'Task'}`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
